import { Component, ErrorInfo, ReactNode } from 'react'
import styles from './errorBoundary.module.scss'

interface ErrorBoundaryProps {
  children: ReactNode
  // Optional custom fallback. When omitted, a compact default is shown.
  fallback?: ReactNode
  // Shown in the default fallback and logged, to identify which area failed.
  label?: string
}

interface ErrorBoundaryState {
  error: Error | null
}

// Contains render-time errors so one failing widget shows a local fallback
// instead of unmounting the whole React tree (React 18 blanks the entire page
// when an error propagates to the root with no boundary in between).
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the real stack in the console so the failing component and
    // property are identifiable even in production builds.
    console.error(
      `ErrorBoundary${this.props.label ? ` [${this.props.label}]` : ''} caught:`,
      error,
      info.componentStack
    )
  }

  handleReset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    const { children, fallback, label } = this.props

    if (!error) return children
    if (fallback) return fallback

    return (
      <div className={styles.fallback} role="alert">
        <span className={styles.title}>
          {label ? `${label} failed to load` : 'Something went wrong'}
        </span>
        <span className={styles.detail}>{error.message}</span>
        <button
          className={styles.retry}
          onClick={this.handleReset}
          type="button"
        >
          Try again
        </button>
      </div>
    )
  }
}
